/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useRef, useState } from 'react';

import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { getStoredToken, authApi } from '@tankef/api-client';
import { useHelpSettings, SoundProvider } from '@tankef/core-react';
import {
  FloatingHelpButton,
  HelpChat,
  getHelpContext,
  Logo,
  NavToggleButton,
  cn,
} from '@tankef/ui';
import { useWebSocketConnection, wsClient } from '@tankef/websocket';

import {
  NavVertical,
  NavMobile,
  HeaderSection,
  DashboardContent,
  NotificationToasts,
  GlobalAlerts,
  MandatoryNotificationGate,
} from './components';
import { NavContext } from './nav-context';
import { NotificationsProvider } from './notifications-context';
import { PageHeaderProvider } from './page-header';
import { useOnboardingAnnouncements } from './useOnboardingAnnouncements';

import { TopAnnouncementBar } from '@/domains/experience/dashboard/ui';
import { RealtimeGateProvider } from '@/providers/RealtimeGateProvider';
import { appSoundResolver } from '@/providers/soundResolver';
import { AppTourController, useTourContext } from '@/tours/AppTourController';

export { usePageHeader, type Breadcrumb, type PageHeaderState } from './page-header';
export type { RoutePageMeta } from './page-meta';

function HelpChatConnected(props: Omit<Parameters<typeof HelpChat>[0], 'onStartTour'>) {
  const { startTourById } = useTourContext();
  return <HelpChat {...props} onStartTour={startTourById} />;
}

/**
 * Layout shell — flex column with a single scroll container (`<main>`).
 *
 * Structure:
 *   ┌─────────────────────────────────┐  h-screen
 *   │ Announcement bar (optional)     │  normal block, no `fixed`
 *   ├───────────┬─────────────────────┤
 *   │           │ Sticky HeaderBar    │
 *   │ Sidebar   ├─────────────────────┤
 *   │           │ Content (scrolls)   │
 *   └───────────┴─────────────────────┘
 *
 * The window never scrolls — `<main>` does. That means the announcement bar
 * stays visible, the sidebar is always full-height, and new top-level slots
 * (banners, impersonation strips, etc.) don't require offset plumbing.
 */
const NAV_COLLAPSED_KEY = 'tankef:nav:collapsed';

export function DashboardLayout() {
  const [isNavCollapsed, setIsNavCollapsed] = useState(
    () => localStorage.getItem(NAV_COLLAPSED_KEY) === 'true'
  );
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { announcements, dismiss } = useOnboardingAnnouncements();
  const { help } = useHelpSettings();
  const mainRef = useRef<HTMLElement>(null);
  const { sectionId: helpSectionId, greeting: helpGreeting } = getHelpContext(location.pathname);

  // Pass the getter so reconnects always read the latest token from localStorage
  // (the HTTP auth interceptor may refresh it without re-rendering this component).
  useWebSocketConnection(getStoredToken);

  // Wire up token refresh on WS auth rejection (covers app-resume after background sleep).
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    wsClient.onAuthRejected = async () => {
      await authApi.refreshToken().catch(() => {});
    };
    return () => {
      wsClient.onAuthRejected = null;
    };
  }, []);

  // Auto-close mobile nav on route change
  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  // Reset scroll on route change — we scroll the main container, not window.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);

  return (
    <PageHeaderProvider>
      <NavContext.Provider
        value={{ openMobileNav: () => setIsMobileNavOpen(true), isMobileNavOpen }}
      >
        {/* Single WS subscription for notifications — prevents duplicate handlers */}
        <NotificationsProvider>
          <SoundProvider resolver={appSoundResolver} volume={0.5}>
            <AppTourController>
              {/* Realtime KYC + balance events → toasts + query invalidation */}
              <RealtimeGateProvider />

              {/* Toast notifications for new user notifications */}
              <NotificationToasts />

              {/* Blocking overlay for mandatory compliance/security notifications */}
              <MandatoryNotificationGate />

              {/* Floating help button — bottom-right, hidden when user turns it off */}
              <FloatingHelpButton
                onClick={() => setIsHelpOpen(true)}
                hidden={!help.helpButtonVisible}
              />
              <HelpChatConnected
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                onNavigate={path => navigate(path)}
                initialSectionId={helpSectionId}
                greeting={helpGreeting}
              />

              {/* Mobile Navigation Drawer */}
              <NavMobile isOpen={isMobileNavOpen} onClose={() => setIsMobileNavOpen(false)} />

              <div className="flex h-screen flex-col bg-tkf-surface-page">
                {/* Announcement bar (KYC, CLABE, etc.) — normal block at the top */}
                {announcements.length > 0 && (
                  <TopAnnouncementBar announcements={announcements} onDismiss={dismiss} />
                )}

                {/* System-level alerts (SPEI down, maintenance notices) */}
                <GlobalAlerts />

                {/* Full-width header: logo section (= sidebar width) + title/controls section */}
                <div className="flex shrink-0 h-16 lg:h-[72px] bg-tkf-surface border-b border-tkf-line z-nav">
                  {/* Logo — hidden on mobile, same width as sidebar on desktop */}
                  <div
                    className={cn(
                      'hidden lg:flex shrink-0 items-center border-r border-tkf-line',
                      'transition-[width] duration-200 ease-out',
                      isNavCollapsed ? 'w-20 justify-center px-2' : 'w-[280px] px-6'
                    )}
                  >
                    <Link to="/dashboard" className="flex items-center">
                      <Logo isSingle={isNavCollapsed} disableLink />
                    </Link>
                  </div>

                  {/* Page title + user controls — fills remaining width */}
                  <HeaderSection onMenuClick={() => setIsMobileNavOpen(true)} />
                </div>

                <div className="flex min-h-0 min-w-0 flex-1">
                  {/* Desktop Sidebar + toggle (toggle is absolute within this relative wrapper;
              `flex flex-col` gives the child NavVertical a definite height to fill;
              a high z-index keeps the collapse handle above the content). */}
                  <div className="relative z-nav flex shrink-0 flex-col">
                    <NavVertical collapsed={isNavCollapsed} />
                    <NavToggleButton
                      placement="absolute"
                      isCollapsed={isNavCollapsed}
                      onClick={() => {
                        const next = !isNavCollapsed;
                        setIsNavCollapsed(next);
                        localStorage.setItem(NAV_COLLAPSED_KEY, String(next));
                      }}
                    />
                  </div>

                  {/* Main content column (the only scroll container) */}
                  <DashboardContent mainRef={mainRef}>
                    <div className="px-4 py-4 lg:px-6 lg:py-6">
                      <Outlet />
                    </div>
                  </DashboardContent>
                </div>
              </div>
            </AppTourController>
          </SoundProvider>
        </NotificationsProvider>
      </NavContext.Provider>
    </PageHeaderProvider>
  );
}
